import { action, computed, observable } from 'mobx'
import { create, persist } from 'mobx-persist'
import { Container, Singleton } from 'typescript-ioc/es5'
import { SidebarState } from './sidebar'
import { fetchAllDomains } from '../_utils/utils'
import { DomainDto } from '../dtos/domain'
import { UserDto } from '../dtos/user'
import { PlatformMessageDto } from '../dtos/platform-message'
import { carryOverLegacyLastUsedDomain, getAccessOwner } from '../core/access/access-singleton'
import type { AppAccessOwner } from '../core/access/access-owner-provider'
import { AppActionContext } from '../enums/app-action-context'
import { WhitelabelConfigModel } from '../models/whitelabel-config/whitelabel-config.model'
import * as Cookie from 'cookie'
import aqe, { SessionState } from '@pushly/aqe'
import { AbilityStore } from './app-ability'
import { addVisibilityAwareIntervalRunner, VisibilityAwareIntervalCleaner } from '../_utils/visibility-api'
import { PlatformUserDomainRecord } from '../models/domain/platform-user-domain-record'
import { ErrorKind, classifyError } from '../core/error-kind'
import { NoDomainsError, UserDependenciesError } from '../core/access/no-domains-error'

export { NoDomainsError, UserDependenciesError }

@Singleton
export class AppState {
    @observable
    public abilityStore: AbilityStore

    @observable
    public loadingCount: any = 1

    @observable
    public moduleLoadingCount: number = 0

    public get moduleLoading(): boolean {
        return this.moduleLoadingCount !== 0
    }

    @observable
    public isLoggingOut: boolean = false

    @persist
    @observable
    public locale: string = 'en'

    @observable
    public sidebar: SidebarState

    @observable
    public platformMessages: PlatformMessageDto[] = []

    @persist
    @observable
    public userPlatformMessages: any

    @persist
    @observable
    public notifChannelSelection: string

    @observable
    public usingNewNotifUx: boolean = true

    @observable
    public currentUser?: UserDto

    @observable
    public currentUserDomains?: PlatformUserDomainRecord[]

    @observable
    public whiteLabelSettings?: WhitelabelConfigModel

    public isVersionCurrent: boolean = true

    private userDependencyFetcher: Promise<void> | undefined
    /** Identifies the domain list load whose answer may still be applied. */
    private userDependencyGeneration = 0

    @computed
    public get platformDisplayName(): string {
        return this.whiteLabelSettings?.getDisplayName() ?? 'Pushly'
    }

    @computed
    public get documentationLink(): string {
        return this.whiteLabelSettings?.getDocumentationLink() ?? 'https://documentation.pushly.com'
    }

    @computed
    public get platformHost(): string {
        const hostname =
            this.whiteLabelSettings?.getHostname() ?? aqe.defaults.publicPlatformDomain?.replace(/^http[s]?:\/\//i, '')

        return `https://${hostname}`
    }

    /**
     * The open domain, owned by the access owner. It is the last domain that resolved, so the app
     * chrome keeps it while the URL names a domain the user cannot open. It is `undefined` before
     * the first resolution, and the same object until a new resolution or refresh replaces it.
     */
    public get currentDomain(): DomainDto | undefined {
        return this.accessOwner.lastResolvedDomain
    }

    /** The open domain as a string; it changes exactly when the open domain's data changes. */
    @computed
    public get currentDomainJsonData(): string | undefined {
        const domain = this.currentDomain
        return domain ? JSON.stringify(domain) : undefined
    }

    /** The context the URL named: an organization or a domain. A domain until a URL has resolved. */
    public get actionContext(): AppActionContext {
        return this.accessOwner.context?.kind === 'org' ? AppActionContext.ORG : AppActionContext.DOMAIN
    }

    /** 1 while the URL names a domain or organization the user cannot open, else 0. */
    public get showing404Count(): number {
        return this.accessOwner.resolution.status === 'unavailable' ? 1 : 0
    }

    /** True while the access owner resolves the URL. */
    public get isDomainSwitching(): boolean {
        return this.accessOwner.isSwitching
    }

    @computed
    public get appIsLoading(): boolean {
        return this.loadingCount !== 0
    }

    @computed
    public get inOrgContext(): boolean {
        return this.actionContext === AppActionContext.ORG
    }

    @computed
    public get inDomainContext(): boolean {
        return this.actionContext === AppActionContext.DOMAIN
    }

    private rehydrater: Promise<any> | undefined
    private userDependencyRefresherCleaner: VisibilityAwareIntervalCleaner | undefined

    /**
     * Takes the access owner once. This constructor is the one place the legacy layer reaches the
     * owner's module singleton; everything on this store, and the services that hold it, reads the
     * field. A test passes its own owner.
     */
    public constructor(public readonly accessOwner: AppAccessOwner = getAccessOwner()) {
        this.sidebar = new SidebarState()
        this.abilityStore = new AbilityStore(this)
        this.initializer
    }

    public get initializer() {
        return this.rehydrate()
    }

    /**
     * Loads the user's domains and hands the list to the access owner, which decides what is
     * open. When a domain is open its detail is refreshed. Concurrent callers share one request.
     * A failed load is never kept: the next call retries. It rejects with a UserDependenciesError, so
     * a caller can tell a failed load from a user who has no domains.
     */
    @action.bound
    public fetchUserDependencies(): Promise<void> {
        if (!this.userDependencyFetcher) {
            const fetcher: Promise<void> = this.loadUserDependencies().finally(() => {
                if (this.userDependencyFetcher === fetcher) {
                    this.userDependencyFetcher = undefined
                }
            })
            this.userDependencyFetcher = fetcher
        }

        return this.userDependencyFetcher
    }

    /**
     * Gives up on a domain list load that is still in flight: the next `fetchUserDependencies` makes its
     * own request, and the abandoned one changes nothing when it lands.
     */
    public abandonUserDependencies(): void {
        this.userDependencyGeneration++
        this.userDependencyFetcher = undefined
    }

    /**
     * Reloads the user dependencies after a good load. A failure keeps the last good domain list and
     * open domain and marks them stale instead of throwing; a later success clears the mark. A user
     * who has no domains any more is an answer, not a failure, so it clears the mark too.
     */
    @action.bound
    public async refreshUserDependencies(): Promise<void> {
        const owner = this.accessOwner
        try {
            await this.fetchUserDependencies()
        } catch (err) {
            if (!(err instanceof NoDomainsError)) {
                const failure =
                    err instanceof UserDependenciesError ? err : new UserDependenciesError(classifyError(err), err)
                if (failure.kind === 'aborted') {
                    return
                }
                console.warn('user_dependencies_refresh_error', failure)
                owner.markStale('domains', failure.kind, failure.cause ?? failure)
                return
            }
        }
        owner.clearStale('domains')
    }

    private async loadUserDependencies(): Promise<void> {
        const generation = this.userDependencyGeneration
        const user = this.currentUser
        try {
            const domainData = await fetchAllDomains({
                query: {
                    fields: [
                        'id',
                        'name',
                        'display_name',
                        'account_id',
                        'account_name',
                        'account_flags',
                        'timezone',
                        'flags',
                        'default_icon_url',
                        'native_apns_configuration',
                        'native_fcm_configuration',
                        'ecomm_config',
                        'is_demo',
                        'is_demo_template',
                        'account_is_demo',
                        'account_is_demo_template',
                    ],
                },
            })
            // A load abandoned, or started for a user who has since signed out or been replaced, applies nothing.
            if (generation !== this.userDependencyGeneration || this.currentUser?.id !== user?.id) {
                throw new UserDependenciesError('aborted', new Error('The user dependency load was superseded'))
            }
            const domains: PlatformUserDomainRecord[] = domainData.data
            this.currentUserDomains = domains
            const owner = this.accessOwner
            owner.setDomains(domains)

            if (domains.length === 0) {
                throw new NoDomainsError()
            }

            const open = owner.resolution
            if (open.status === 'resolved') {
                await owner.refreshOpenDomain(open.domain.id)
            }
        } catch (err) {
            throw err instanceof UserDependenciesError ? err : new UserDependenciesError(classifyError(err), err)
        }

        this.setupUserDependencyFetchRunner()
    }

    private setupUserDependencyFetchRunner() {
        if (!this.userDependencyRefresherCleaner) {
            const [cleaner] = addVisibilityAwareIntervalRunner(
                'user-dependencies',
                () => this.refreshUserDependencies(),
                300000,
            )
            this.userDependencyRefresherCleaner = cleaner
        }
    }

    /** Stops the periodic user-dependencies refresh. The next successful load starts it again. */
    public stopUserDependencyRefresher(): void {
        this.userDependencyRefresherCleaner?.()
        this.userDependencyRefresherCleaner = undefined
    }

    private async rehydrate() {
        if (!this.rehydrater) {
            this.rehydrater = new Promise<void>(async (res, rej) => {
                try {
                    carryOverLegacyLastUsedDomain()

                    const hydrate = create({
                        storage: localStorage,
                        jsonify: true,
                    })

                    await hydrate('pufferfish', this)
                    await hydrate('pufferfish.session', Container.get(SessionState))
                    await hydrate('pufferfish.sidebar-v2', this.sidebar)

                    this.removeLegacyToken()
                } catch (error) {
                    rej(error)

                    return
                }

                res()
            })
        }

        return this.rehydrater
    }

    private removeLegacyToken() {
        try {
            const cookies = Cookie.parse(document.cookie)
            if ('swptk' in cookies) {
                const now = new Date()
                const yesterday = now.setDate(now.getDate() - 1)
                document.cookie = `swptk=null;path=/;domain=${
                    aqe.defaults.storageDomain
                };expires=${yesterday.toString()};max-age=0;`
            }
        } catch (_) {}
    }
}
